import { useEffect, useState } from 'react';
import { fetchTopScores, recordEvent, type Score } from '../../features/ranking/api';
import { Button } from '../../components/ui/Button';

const METRICS = [
  { id: 'posts', label: 'Forum Posts', icon: '💬' },
  { id: 'replies', label: 'Replies', icon: '💭' },
  { id: 'helpful', label: 'Helpful Votes', icon: '👍' },
  { id: 'listings', label: 'Marketplace Listings', icon: '🏪' },
  { id: 'bookings', label: 'Completed Bookings', icon: '✅' },
  { id: 'reputation', label: 'Overall Reputation', icon: '⭐' },
];

export function RankingPage() {
  const [selectedMetric, setSelectedMetric] = useState('reputation');
  const [scores, setScores] = useState<Score[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    loadScores();
  }, [selectedMetric, limit]);

  async function loadScores() {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTopScores(selectedMetric, limit);
      setScores(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rankings');
    } finally {
      setLoading(false);
    }
  }

  const currentMetric = METRICS.find((m) => m.id === selectedMetric);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">Community Rankings</h1>
          <p className="text-slate-600 dark:text-slate-400">Top contributors across various metrics</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Metric Selection */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase mb-3">Metrics</h2>
              <div className="space-y-1">
                {METRICS.map((metric) => (
                  <button
                    key={metric.id}
                    onClick={() => setSelectedMetric(metric.id)}
                    className={`w-full text-left px-3 py-2 rounded transition-colors ${
                      selectedMetric === metric.id
                        ? 'bg-brand-600 text-white'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <span className="mr-2">{metric.icon}</span>
                    {metric.label}
                  </button>
                ))}
              </div>

              {/* Limit Selector */}
              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Show Top</label>
                <select
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Test Data Generator */}
            <TestDataGenerator onGenerate={loadScores} />
          </div>

          {/* Leaderboard */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 flex items-center">
                  <span className="text-2xl mr-2">{currentMetric?.icon}</span>
                  {currentMetric?.label} Leaderboard
                </h2>
                <Button onClick={loadScores} variant="secondary" className="text-sm py-1.5 px-3">
                  Refresh
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-12 text-slate-600 dark:text-slate-400">Loading rankings...</div>
              ) : error ? (
                <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
                  Error: {error}
                </div>
              ) : scores.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">🏆</div>
                  <p className="text-slate-600 dark:text-slate-400">No rankings yet for this metric</p>
                  <p className="text-slate-500 dark:text-slate-500 text-sm mt-2">Be the first to contribute!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {scores.map((score, idx) => {
                    const rank = idx + 1;
                    const value = score.values[selectedMetric] || 0;
                    const isTopThree = rank <= 3;

                    return (
                      <div
                        key={`${score.alias}-${idx}`}
                        className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                          isTopThree
                            ? 'bg-gradient-to-r from-yellow-100 to-slate-100 dark:from-yellow-900/20 dark:to-slate-900 border border-yellow-300 dark:border-yellow-800/30'
                            : 'bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Rank Badge */}
                          <div
                            className={`w-10 h-10 flex items-center justify-center rounded-full font-bold ${
                              rank === 1
                                ? 'bg-yellow-600 text-yellow-100'
                                : rank === 2
                                  ? 'bg-slate-400 text-slate-900'
                                  : rank === 3
                                    ? 'bg-orange-700 text-orange-100'
                                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
                          </div>

                          {/* User Info */}
                          <div>
                            <div className="font-semibold text-slate-900 dark:text-slate-100">{score.alias}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-500">
                              {isTopThree && rank === 1 && '👑 Champion'}
                              {isTopThree && rank === 2 && '🌟 Rising Star'}
                              {isTopThree && rank === 3 && '💫 Top Contributor'}
                              {!isTopThree && `Rank #${rank}`}
                            </div>
                          </div>
                        </div>

                        {/* Score */}
                        <div className="text-right">
                          <div className="text-2xl font-bold text-brand-600 dark:text-brand-400">{value.toLocaleString()}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-500">points</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TestDataGenerator({ onGenerate }: { onGenerate: () => void }) {
  const [generating, setGenerating] = useState(false);

  async function generateTestData() {
    setGenerating(true);
    try {
      const aliases = ['Alice', 'Bob', 'Charlie', 'Diana', 'Eve', 'Frank', 'Grace', 'Henry', 'Iris', 'Jack'];
      const metrics = ['posts', 'replies', 'helpful', 'listings', 'bookings', 'reputation'];

      for (const alias of aliases) {
        for (const metric of metrics) {
          const value = Math.floor(Math.random() * 100) + 10;
          await recordEvent(alias, metric, value);
        }
      }

      onGenerate();
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="mt-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Test Data</h3>
      <p className="text-xs text-slate-500 dark:text-slate-500 mb-3">Generate sample ranking data for testing</p>
      <Button
        onClick={generateTestData}
        disabled={generating}
        className="w-full bg-green-700 hover:bg-green-600 text-sm py-1.5"
      >
        {generating ? 'Generating...' : 'Generate Test Data'}
      </Button>
    </div>
  );
}
